import { differenceInCalendarDays, format, parseISO, startOfDay } from 'date-fns'
import { ptBR } from 'date-fns/locale'

import { type FaseCiclo } from '@/tipos/dominio'

/** Data de hoje no formato `YYYY-MM-DD`, no fuso do dispositivo. */
export function hojeISO(agora = new Date()) {
  return format(agora, 'yyyy-MM-dd')
}

/** Fuso do dispositivo, gravado no perfil e usado para agendar o push. */
export function fusoDoDispositivo() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Sao_Paulo'
}

/**
 * Dia do programa para uma data de início. Pode ser zero ou negativo
 * antes do início e nunca passa de `duracaoDias`.
 *
 * Espelha `public.dia_atual` do banco. Existe no cliente para que a tela
 * inicial não dependa de rede — a participante precisa saber em que dia
 * está mesmo sem sinal.
 *
 * Não tem piso em 1 de propósito: uma matrícula com início no futuro
 * (comum quando a turma é cadastrada com alguns dias de antecedência)
 * precisa mostrar "ainda não começou", não "Dia 1". A versão anterior,
 * com `Math.max(1, ...)`, também fazia os lembretes dispararem antes da
 * hora — a mesma conta é usada em `lembretes_pendentes()` no banco.
 */
export function diaDoPrograma(dataInicio: string | Date, duracaoDias: number, agora = new Date()) {
  const inicio = typeof dataInicio === 'string' ? parseISO(dataInicio) : dataInicio
  const decorridos = differenceInCalendarDays(startOfDay(agora), startOfDay(inicio))
  return Math.min(duracaoDias, decorridos + 1)
}

/** Dias até o início do programa. Zero ou negativo quando já começou. */
export function diasParaComecar(dataInicio: string | Date, agora = new Date()) {
  const inicio = typeof dataInicio === 'string' ? parseISO(dataInicio) : dataInicio
  return differenceInCalendarDays(startOfDay(inicio), startOfDay(agora))
}

/** Percentual de conclusão do programa, arredondado. */
export function percentualPrograma(dia: number, duracaoDias: number) {
  return Math.round((Math.min(Math.max(dia, 0), duracaoDias) / duracaoDias) * 100)
}

/** Semana 1..4 a partir do dia do programa. */
export function semanaDoPrograma(dia: number) {
  return Math.floor((dia - 1) / 7) + 1
}

/** `true` quando o programa já passou do último dia. */
export function programaConcluido(
  dataInicio: string | Date,
  duracaoDias: number,
  agora = new Date(),
) {
  const inicio = typeof dataInicio === 'string' ? parseISO(dataInicio) : dataInicio
  return differenceInCalendarDays(startOfDay(agora), startOfDay(inicio)) + 1 > duracaoDias
}

// ---------------------------------------------------------------------
// Ciclo menstrual (seção 10.1)
// ---------------------------------------------------------------------

const DURACAO_CICLO_PADRAO = 28

/**
 * Fase do ciclo a partir do primeiro dia da menstruação.
 *
 * Divisão de referência num ciclo de 28 dias: menstrual 1–5,
 * folicular 6–13, ovulatória 14–16, lútea 17 em diante. É estimativa
 * educativa, não cálculo clínico — a especificação pede exatamente
 * isso e nada além.
 */
export function faseDoCiclo(
  dataInicioUltimaMenstruacao: string | Date,
  agora = new Date(),
  duracaoCiclo = DURACAO_CICLO_PADRAO,
): { fase: FaseCiclo; diaDoCiclo: number } {
  const inicio =
    typeof dataInicioUltimaMenstruacao === 'string'
      ? parseISO(dataInicioUltimaMenstruacao)
      : dataInicioUltimaMenstruacao

  const decorridos = differenceInCalendarDays(startOfDay(agora), startOfDay(inicio))

  // Normaliza para dentro do ciclo atual, tratando ciclos já vencidos.
  const diaDoCiclo = ((decorridos % duracaoCiclo) + duracaoCiclo) % duracaoCiclo + 1

  if (diaDoCiclo <= 5) return { fase: 'menstrual', diaDoCiclo }
  if (diaDoCiclo <= 13) return { fase: 'folicular', diaDoCiclo }
  if (diaDoCiclo <= 16) return { fase: 'ovulatoria', diaDoCiclo }
  return { fase: 'lutea', diaDoCiclo }
}

// ---------------------------------------------------------------------
// Formatação
// ---------------------------------------------------------------------

export function formatarData(data: string | Date, padrao = "d 'de' MMMM") {
  const d = typeof data === 'string' ? parseISO(data) : data
  return format(d, padrao, { locale: ptBR })
}

export function formatarDataCompleta(data: string | Date) {
  return formatarData(data, "EEEE, d 'de' MMMM 'de' yyyy")
}

/** `08:30` a partir de um `time` do Postgres (`08:30:00`). */
export function formatarHorario(horario: string) {
  return horario.slice(0, 5)
}
